/**
 * A capsule's cover art — a film, or a picture with a voice.
 *
 * A capsule's cover used to *be* a film: the studio picked a video, publishing
 * uploaded it as the post's video embed, and the play screen opened on it. That
 * made a video a requirement for the whole format, which is a strange thing for a
 * learning capsule to demand — a creator with a drawn diagram and a few sentences
 * of explanation had nothing to publish.
 *
 * So the cover is art, and there are two kinds. A **film** plays and hands over to
 * the quiz when it ends. A **picture** is a still the creator speaks over: it takes
 * narration audio (the same `readingAudioUrl` a reading block carries), and the
 * voice is what ends — the capsule opens on the picture and moves on when the
 * creator stops talking. A picture with no voice simply waits for the learner.
 *
 * Three rules live here, in one place, because a cover is read by the studio, the
 * publisher, the drafts list and the play screen, and four opinions about "what
 * kind of art is this" is three too many:
 *
 *  - **A film wins when both are present.** The studio clears one when the other
 *    arrives, so both is a state that only reaches a *record* — one written by a
 *    build that let art be swapped. The film is the post's own embed, so a reader
 *    that could not decide would be a reader that might play the wrong thing.
 *  - **Only a picture takes a voice.** A film brings its own sound; a narration
 *    field under a film would be a control that fights it.
 *  - **Either art opens the capsule.** That is the whole point of the change: a
 *    picture alone is a cover.
 *
 * Reading a cover *off a record* is deliberately only about the picture. A film is
 * the post's video embed — it is not in the capsule record at all — so a module
 * that pretended to answer for one would be inventing a field that does not exist.
 */
import {type AppBskyEmbedVideo} from '@atproto/api'

import {VIDEO_SERVICE} from '#/lib/constants'
import {buildPdsBlobUrl} from '#/lib/document/util'
import {type Slide} from '#/types/capsule'

/**
 * The most bytes a cover picture may be, and the smaller of two declared limits.
 *
 * A picture cover is written in two places, and each declares its own ceiling:
 * the post's `app.bsky.embed.images` allows 2 MB (`lexicons/app/bsky/embed/images.json`)
 * and the capsule record's own `coverImage` allows 1 MB
 * (`lexicons/app/bsky/feed/capsule.json`) — and **the post is validated on write**,
 * because `app.bsky.feed.post` is one of the schemas the PDS knows, so a picture over
 * the limit is an `InvalidRecord` refusal at the last step of publishing. A phone
 * photograph is routinely three to six. So the creator's picture is squeezed to fit
 * *before* it is held anywhere, which is why the studio compresses on pick rather
 * than letting the publish be the thing that discovers the problem.
 */
export const CAPSULE_COVER_PICTURE_MAX_BYTES = 1_000_000

export type CapsuleCoverArt =
  | {kind: 'none'}
  | {
      kind: 'image'
      url: string
      /** The creator's own voice over the picture, when there is one. */
      audioUrl?: string
    }
  | {
      kind: 'video'
      url: string
      /** How long the film runs, when the platform measured it. */
      durationMs?: number
      /** Which minute of a long film the cover opens on. */
      startMs?: number
    }

/**
 * The art a cover slide carries.
 *
 * The cover is the studio's first slide — `slides[0]` — and it is the one slide
 * whose *art* varies rather than its type, which is why this reads fields rather
 * than switching on `slide.type`.
 */
export function capsuleCoverArt(
  slide: Slide | undefined | null,
): CapsuleCoverArt {
  const film = slide?.videoUrl
  if (film) {
    return {
      kind: 'video',
      url: film,
      durationMs: slide?.videoDurationMs,
      startMs: slide?.videoStartMs,
    }
  }
  const picture = slide?.imageUrl
  if (picture) {
    return {kind: 'image', url: picture, audioUrl: slide?.readingAudioUrl}
  }
  return {kind: 'none'}
}

/** Whether the cover has art at all, which is what lets a capsule go on. */
export function coverArtOpens(art: CapsuleCoverArt): boolean {
  return art.kind !== 'none'
}

/**
 * Whether a voice belongs on this art.
 *
 * Asked by the studio (does it draw the narration field?) and by the play screen
 * (does it wait for a voice before the quiz?), so the two cannot disagree about
 * whether a picture is meant to be spoken over.
 */
export function coverArtTakesVoice(art: CapsuleCoverArt): boolean {
  return art.kind === 'image'
}

/**
 * A published picture cover, as an address a learner's device can fetch.
 *
 * The record holds the picture as a **blob** (`coverImage` in the capsule
 * lexicon), so the address is built from the author's DID and the blob's CID.
 * The one tolerance is the shape: a *view* hands the same field over as a ready
 * URI, and a reader that insisted on the blob shape would break the day the
 * AppView started answering with one.
 *
 * `null` for every other cover: a film (which lives on the post, not here), a
 * capsule with no picture, and a blob reference with no CID in it. Each of those
 * is "this record has no picture cover", which is what the caller needs to know.
 */
export function pictureCoverFromRecord(
  value: Record<string, unknown>,
  did: string,
): {url: string; audioUrl?: string} | null {
  const picture = value.coverImage
  if (typeof picture === 'string') {
    if (!picture) return null
    return {url: picture, audioUrl: audioUrlOf(value)}
  }
  if (!picture || typeof picture !== 'object') return null
  const ref = (picture as {ref?: unknown}).ref
  const cid =
    ref && typeof ref === 'object'
      ? (ref as {$link?: unknown}).$link
      : typeof ref === 'string'
        ? ref
        : undefined
  if (typeof cid !== 'string' || !cid) return null
  return {url: buildPdsBlobUrl(did, cid), audioUrl: audioUrlOf(value)}
}

function audioUrlOf(value: Record<string, unknown>): string | undefined {
  const audio = value.coverAudioUrl
  return typeof audio === 'string' && audio ? audio : undefined
}

/*
 * The cover film of a capsule that has no post (one that belongs to a single
 * classroom). A published capsule's film lives on its post as an
 * `app.bsky.embed.video` view; a post-less capsule carries the embed on its
 * record, and since the video service serves a transcoded film at
 * `<service>/vid/<did>/<cid>/...`, the playable view can be derived from it.
 */

/** The cid of the blob an embed points at, however it arrived. */
function blobCidOf(blob: unknown): string | undefined {
  if (!blob || typeof blob !== 'object') return undefined
  const ref = (blob as {ref?: unknown}).ref
  if (!ref) return undefined
  // A bare string (already a cid), a DAG-JSON link from a raw record read, or a
  // parsed `CID`, which stringifies to itself.
  if (typeof ref === 'string') return ref
  const link = (ref as {$link?: unknown})['$link']
  if (typeof link === 'string') return link
  const asString = String(ref)
  return asString && asString !== '[object Object]' ? asString : undefined
}

/**
 * The playable cover an `app.bsky.embed.video` record describes, or `undefined`
 * when there is no film (or nothing that looks like one).
 */
export function capsuleCoverFromEmbed(
  embed: unknown,
  did: string,
): AppBskyEmbedVideo.View | undefined {
  if (!embed || typeof embed !== 'object') return undefined
  const cid = blobCidOf((embed as {video?: unknown}).video)
  if (!cid) return undefined
  const aspectRatio = (embed as {aspectRatio?: {width: number; height: number}})
    .aspectRatio
  const path = `${encodeURIComponent(did)}/${cid}`
  return {
    $type: 'app.bsky.embed.video#view',
    cid,
    playlist: `${VIDEO_SERVICE}/vid/${path}/playlist.m3u8`,
    thumbnail: `${VIDEO_SERVICE}/vid/${path}/thumbnail.jpg`,
    ...(aspectRatio ? {aspectRatio} : null),
  }
}
